import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { Accordion, AccordionSummary, Box } from '@mui/material';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ActionButton from '../../../elements/action-button/ActionButton';
import Text from '../../../elements/text/Text';
import { schema } from '../crawler-stepper/crawlerFormSchema';
import CrawlStep2 from '../crawler-stepper/CrawlStep2';
import CrawlStep3 from '../crawler-stepper/CrawlStep3';
import CrawlStep4 from '../crawler-stepper/CrawlStep4';
import CrawlStep5 from '../crawler-stepper/CrawlStep5';

const EditCrawlerJob = ({
  instances,
  job,
  getMembers,
  getCollections,
  getUniqueKey,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();

  const formMethods = useForm({
    defaultValues: job,
    resolver: yupResolver(schema()),
  });

  const handleClose = () => onClose();

  const {
    formState: { errors },
  } = formMethods;

  const jobSettingsError =
    !!errors?.jobName ||
    !!errors?.user ||
    !!errors?.seeds ||
    !!errors?.subseeds;
  const extraSettingsError =
    !!errors?.includeInCrawl || !!errors?.includeInIndex;
  const solrSettingsError =
    !!errors?.instance ||
    !!errors?.collection ||
    !!errors?.uniqueKey ||
    !!errors?.updateHandler ||
    !!errors?.removeHandler ||
    !!errors?.statusHandler ||
    !!errors?.commitTime ||
    !!errors?.commitAfterJob ||
    !!errors?.crawlArgs;
  const scheduleSettingsError =
    !!errors?.periodType ||
    !!errors?.periodTime ||
    !!errors?.day ||
    !!errors?.date;

  const titles = [
    [t('crawler.jobSettings', { ns: 'sectionTitles' }), jobSettingsError],
    [t('crawler.extraSettings', { ns: 'sectionTitles' }), extraSettingsError],
    [t('crawler.solrSettings', { ns: 'sectionTitles' }), solrSettingsError],
    [
      t('crawler.scheduleSettings', { ns: 'sectionTitles' }),
      scheduleSettingsError,
    ],
  ];

  return (
    <FormProvider {...formMethods}>
      <form>
        {[
          <CrawlStep2 key={1} getMembers={getMembers} />,
          <CrawlStep3 key={2} />,
          <CrawlStep4
            key={3}
            getCollections={getCollections}
            getUniqueKey={getUniqueKey}
            instances={instances}
          />,
          <CrawlStep5 key={4} />,
        ].map((step, i) => (
          <Accordion key={i}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {titles[i][1] && (
                <WarningIcon color="error" style={{ marginRight: '0.5em' }} />
              )}
              <Text color={titles[i][1] ? 'error' : 'inherit'}>
                {titles[i][0]}
              </Text>
            </AccordionSummary>
            <Box sx={{ p: 2 }}>{step}</Box>
          </Accordion>
        ))}
        <Box
          sx={{ mt: 2, gap: 2, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button variant="text" type="reset" onClick={handleClose}>
            {t('cancel', { ns: 'buttons' })}
          </Button>
          <ActionButton clickAction={formMethods.handleSubmit(onSubmit)}>
            {t('confirm', { ns: 'buttons' })}
          </ActionButton>
        </Box>
      </form>
    </FormProvider>
  );
};

EditCrawlerJob.propTypes = {
  instances: PropTypes.array,
  job: PropTypes.object,
  processing: PropTypes.bool,
  getMembers: PropTypes.func.isRequired,
  getCollections: PropTypes.func.isRequired,
  getUniqueKey: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditCrawlerJob;
